import axios from "axios";
import { setToast } from "../../src/shared/store/slice/toastSlice";
import { store } from "../../src/shared/store/store";
import CryptoJS from "crypto-js";

// Create axios instance with base URL from environment variable
const API = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

const SIGN_SECRET = "QD32VdbRuMa0iI0q9q7cH6FIHGcNWGdEZOLyK669";

function getHashCode(data: string) {
  let hash = 0, char;
  if (data.length === 0) return 0;
  for (let i = 0; i < data.length; i++) {
    char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

function encodeQueryData(data: any) {
  if (!data) return '';
  const ret: string[] = [];
  for (const d in data) {
    if (data[d] !== undefined && data[d] !== null) {
      ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d] as string));
    }
  }
  return ret.join('&');
}

function replaceChar(str: string) {
  if (str != null) {
    str = str.replace(/%5B/g, '[');
    str = str.replace(/%5D/g, ']');
    str = str.replace(/%2C/g, ',');
    str = str.replace(/filters\[id\]\[_eq\]=42&/g, '');
  }
  return str;
}

function getSignature(inUrl: string, timeStamp: string, payLoadData: any, queryParam: string) {
  queryParam = (!queryParam || queryParam === 'null' || queryParam === '' || Object.keys(queryParam).length === 0) ? 'null' : queryParam;
  let _details = queryParam + timeStamp;
  _details = _details + getHashCode(_details);
  payLoadData = (payLoadData === undefined || payLoadData === null || (typeof payLoadData === 'object' && Object.keys(payLoadData).length === 0)) ? 'null' : JSON.stringify(payLoadData);
  _details = _details + payLoadData;
  const urlStrArray = inUrl.split('/');
  const Data = urlStrArray[4] + timeStamp + (urlStrArray[5] === undefined || urlStrArray[5] === null ? 'null' : urlStrArray[5]) + (urlStrArray[6] === undefined || urlStrArray[6] === null ? 'null' : urlStrArray[6]) + _details;
  const hash1 = CryptoJS.HmacSHA256(Data, SIGN_SECRET);
  const hashInBase64 = CryptoJS.enc.Base64.stringify(hash1);
  return hashInBase64;
}

// Function to refresh the token
const refreshAccessToken = async () => {
  try {
    const storedData = localStorage.getItem('data');
    if (!storedData) {
      throw new Error('No data found in localStorage');
    }

    const data = JSON.parse(storedData);
    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/um/refresh-token`, {
      refresh_token: data.refreshToken
    });  
    if (response?.data?.status === "success" && response?.data?.data) {
      // Update localStorage with new tokens and user data
      const updatedData = {
        ...data,
        token: response.data.data.access_token,
        refreshToken: response.data.data.refresh_token,
      };
      localStorage.setItem('data', JSON.stringify(updatedData));
      return response.data.data.access_token;
    }
    throw new Error('Invalid refresh token response');
  } catch (error) {
    // Clear localStorage and redirect to login
    localStorage.removeItem("data");
    store.dispatch(
      setToast({
        message: "Session expired. Please log in again.",
        severity: "error",
        open: true,
      })
    );
    window.location.href = "/account/login";
    throw error;
  }
};

// Add a request interceptor
API.interceptors.request.use(
  function (config) {
    // Get token and language from localStorage
    const data:any = JSON.parse(localStorage.getItem('data') || '{}');
    const token = data?.token;
    const language = localStorage.getItem('language')|| 'en';
    
    // Set language header based on localStorage value
    config.headers["Accept-Language"] = language === 'hi' ? 'hi' : 'en';
    
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // --- Signature and Timestamp logic ---
    const timeStamp = Date.now().toString();

    // Build the full URL
    let baseUrl = config.baseURL || '';
    const url = config.url || '';
    if (url.startsWith('http')) {
      baseUrl = '';
    }
    let fullUrl = baseUrl + url;

    // Query params
    let queryParam = '';
    if (config.params) {
      queryParam = encodeQueryData(config.params);
      queryParam = queryParam ? replaceChar(queryParam) : '';
      if (queryParam) {
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryParam;
      }
    }

    // Payload
    let payLoadData = config.data;
    if (typeof payLoadData === 'string') {
      try { payLoadData = JSON.parse(payLoadData); } catch { /* ignore */ }
    }

    // Signature
    const signature = getSignature(fullUrl, timeStamp, payLoadData, queryParam);

    config.headers["timeStamp"] = timeStamp;
    config.headers["Signature"] = signature;
    config.headers["source"] ="web";

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Variables to handle refresh token state
let isRefreshing = false;
let refreshSubscribers: any = [];

// Function to notify subscribers once the token is refreshed
const onRefreshed = (newToken: any) => {
  refreshSubscribers.forEach((callback: any) => callback(newToken));
  refreshSubscribers = [];
};

// Add a response interceptor
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const dispatch = store.dispatch;
    const originalRequest = error.config;
    const url = window.location.pathname;

    // Handle login page errors differently
    if (url?.includes("account/login")) {
      dispatch(
        setToast({
          message: error?.response?.data?.message || "Authentication failed",
          severity: "error",
          open: true,
        })
      );
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          onRefreshed(newToken);
          return API(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = []; // Clear subscribers
          
          // Handle the error response format
          const errorData = error.response?.data;
          dispatch(
            setToast({
              message: errorData?.message || "Session expired. Please log in again.",
              severity: "error",
              open: true,
            })
          );
          localStorage.removeItem("data");
          window.location.href = "/account/login";
          return Promise.reject(refreshError);
        }
      }

      // If token refresh is in progress, queue the request
      return new Promise((resolve) => {
        refreshSubscribers.push((newToken: any) => {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          resolve(API(originalRequest));
        });
      });
    }

    // Handle other error responses
    if (error.response) {
      const errorData = error.response.data;
    
      switch(error.response.status){
        case 401:{
          dispatch(
            setToast({
              message: errorData?.message || "An error occurred",
              severity: "error",
              open: true,
            })
          )
          if (error?.response?.data?.code=="AUTH_EXPIRED_TOKEN"){
            localStorage.removeItem("data");
            window.location.replace('/account/login');
          }
        }
      }

      /*
       switch (error.response.status) {
        case 400:
          dispatch(
            setToast({
              message: errorMessage,
              severity: "error",
              open: true,
            })
          );
          break;
        case 403:
          dispatch(
            setToast({
              message: errorMessage || "You don't have permission to access this resource",
              severity: "error",
              open: true,
            })
          );
          break;
        case 404:
          dispatch(
            setToast({
              message: errorMessage || "The requested resource was not found",
              severity: "error",
              open: true,
            })
          );
          break;
        case 500:
          dispatch(
            setToast({
              message: errorMessage || "Internal server error. Please try again later",
              severity: "error",
              open: true,
            })
          );
          break;
        default:
          dispatch(
            setToast({
              message: errorMessage,
              severity: "error",
              open: true,
            })
          );
      }
       */
      dispatch(
        setToast({
          message: errorData?.message || "An error occurred",
          severity: "error",
          open: true,
        })
      );
    } else {
      // Handle network errors
      dispatch(
        setToast({
          message: "Network error: Please check your internet connection",
          severity: "error",
          open: true,
        })
      );
    }

    return Promise.reject(error);
  }
);

export default API; 